import { useMutation } from '@apollo/client';
import { useFormikContext } from 'formik';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'reactstrap';
import styled from 'styled-components';
import { useFarmSettingsContext } from '../../contexts/FarmSettingsContext';
import { useFarmSourceContext } from '../../contexts/FarmSourceContext';
import {
  ArchiveFarmMutation,
  DeleteFarmMutation,
  DuplicateFarmMutation,
  LockFarmMutation,
  UnarchiveFarmMutation,
  UnlockFarmMutation,
} from '../../mutations';
import {
  useIsFarmView,
  useIsSuperadminRoute,
  useUserFarmActions,
  useUserRole,
} from '../../utils/hooks';
import Delayed from '../basics/Delayed';
import ShareFarmButton from '../ShareFarmButton';
import { useIsMobile } from '../../utils/responsive';
import { captureEvent } from '../../utils/analytics';
import { farmCanBeDuplicated } from '../../utils/utils';
import useNavigateToFarm from '../../hooks/useNavigateToFarm';
import { SubmitButton } from '../templates/template-components';

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  margin-top: ${({ marginTop }) => marginTop || '8px'};
`;

const FitContentDiv = styled.div`
  min-width: fit-content;
`;

const FullWidthButton = styled(Button)`
  width: 100%;
  text-wrap: nowrap;
`;

const FullWidthSubmitButton = styled(SubmitButton)`
  width: 100%;
  text-wrap: nowrap;
`;

const DeleteButtonWithConfirm = ({
  farm,
  isDeleting,
  setIsDeleting,
  mutateAndSetState,
}) => {
  const { source } = useFarmSourceContext();
  const isOnFarm = useIsFarmView();
  const isSuperadminRoute = useIsSuperadminRoute();
  const navigate = useNavigate();
  const { farmRefetchQuery, closeFarmSettingsModal } = useFarmSettingsContext();
  const [deleteMutation] = useMutation(DeleteFarmMutation, {
    refetchQueries: farmRefetchQuery
      ? [{ query: farmRefetchQuery, variables: { teamId: farm.teamId } }]
      : [],
    onCompleted: (data) => {
      if (data.deleteFarm.result) {
        closeFarmSettingsModal();
        if (isOnFarm) {
          // if we were on a farm page, navigate back to the farms table since that farm no longer exists
          navigate(source || '/app');
        }
        if (isSuperadminRoute) {
          // if we were on a superadmin page, navigate back to the farms table since that farm no longer exists
          navigate('/superadmin/farms');
        }
      }
    },
  });

  // show delete button if not yet deleting
  if (!isDeleting) {
    return (
      <FullWidthButton
        type="button"
        onClick={() => {
          setIsDeleting(true);
        }}
        color="danger"
        outline
      >
        Delete
      </FullWidthButton>
    );
  }
  // show confirm/cancel buttons when deleting
  return (
    <ButtonRow className="w-100 align-items-center" marginTop="0px">
      <FitContentDiv className="text-center">Are you sure?</FitContentDiv>
      <FullWidthButton
        onClick={() => {
          setIsDeleting(false);
        }}
      >
        Cancel
      </FullWidthButton>
      <FullWidthButton
        color="danger"
        outline
        onClick={() => {
          captureEvent('farm-deleted', { location: 'project settings' });
          mutateAndSetState(() =>
            deleteMutation({
              variables: { input: { id: farm.id, updatedAt: farm.updatedAt } },
            }),
          );
          setIsDeleting(false);
        }}
      >
        Delete Permanently
      </FullWidthButton>
    </ButtonRow>
  );
};

const ArchiveOrUnarchiveButton = ({ farm, mutateAndSetState }) => {
  const [archiveOrUnarchiveMutation] = useMutation(
    farm.isArchived ? UnarchiveFarmMutation : ArchiveFarmMutation,
  );

  return (
    <FullWidthButton
      type="button"
      onClick={() => {
        captureEvent(farm.isArchived ? 'farm-unarchived' : 'farm-archived', {
          location: 'project settings',
        });
        mutateAndSetState(
          () =>
            archiveOrUnarchiveMutation({
              variables: { input: { id: farm.id, updatedAt: farm.updatedAt } },
            }),
          farm.isArchived
            ? `Restored ${farm.name} successfully`
            : `Archived ${farm.name} successfully`,
        );
      }}
      color={farm.isArchived ? 'secondary' : 'danger'}
      outline={!farm.isArchived}
    >
      {farm.isArchived ? 'Unarchive' : 'Archive'}
    </FullWidthButton>
  );
};

const LockOrUnlockButton = ({ farm, mutateAndSetState }) => {
  const [lockOrUnlockMutation] = useMutation(
    farm.isLocked ? UnlockFarmMutation : LockFarmMutation,
  );

  return (
    <FullWidthButton
      type="button"
      onClick={() => {
        captureEvent(farm.isLocked ? 'farm-unlocked' : 'farm-locked', {
          location: 'project settings',
        });
        mutateAndSetState(
          () =>
            lockOrUnlockMutation({
              variables: { input: { id: farm.id, updatedAt: farm.updatedAt } },
            }),
          farm.isLocked
            ? `Unlocked ${farm.name} successfully`
            : `Locked ${farm.name} successfully`,
        );
      }}
      color="secondary"
    >
      {farm.isLocked ? 'Unlock' : 'Lock'}
    </FullWidthButton>
  );
};

const ViewFarmButton = ({ farm, loading }) => {
  const { pathname } = useLocation();
  const { closeFarmSettingsModal } = useFarmSettingsContext();
  const navigateToFarm = useNavigateToFarm();

  return (
    <FullWidthButton
      type="button"
      onClick={() => {
        closeFarmSettingsModal();
        navigateToFarm(farm.id, { state: { source: pathname } });
      }}
      disabled={loading}
    >
      View Farm
    </FullWidthButton>
  );
};

const DuplicateFarmButton = ({ farm, mutateAndSetState, loading }) => {
  const isOnFarm = useIsFarmView();
  const { source } = useFarmSourceContext();
  const { pathname } = useLocation();
  const { closeFarmSettingsModal } = useFarmSettingsContext();
  const navigateToFarm = useNavigateToFarm();
  const [duplicateFarm] = useMutation(DuplicateFarmMutation, {
    onCompleted: (data) => {
      if (data.duplicateFarm.result?.id) {
        closeFarmSettingsModal();
        navigateToFarm(data.duplicateFarm.result?.id, {
          state: { source: isOnFarm ? source : pathname },
        });
      }
    },
  });

  return (
    <FullWidthButton
      type="button"
      onClick={() => {
        return mutateAndSetState(() =>
          duplicateFarm({ variables: { farmId: farm.id } }),
        );
      }}
      disabled={loading}
      color="secondary"
    >
      Duplicate
    </FullWidthButton>
  );
};

const FarmFormButtonBlock = ({
  farm,
  onCancel,
  userCanEditFarm,
  mutateAndSetState,
  loading,
  inModal,
  submitButtonText,
}) => {
  const { canShare, canDelete, canArchive, canLock } = useUserFarmActions(
    farm.id,
  );
  const isMobile = useIsMobile();

  const { isSubmitting } = useFormikContext();

  const [isDeleting, setIsDeleting] = useState(false);

  // ---- Row 1 ----
  // a farm can be viewed if it is not a new farm and the page is not currently a farm page
  const showViewButton = !useIsFarmView() && farm.id;
  // a farm can be archived/unarchived if it is not a new farm and the user has permission to archive
  // and the deletion confirmation flow is not in progress

  // ---- Row 2 ----
  const showArchiveUnarchiveButton = farm.id && canArchive && !isDeleting;
  // a farm can be locked/unlocked if it is not a new farm and the user has permission to lock
  // and the deletion confirmation flow is not in progress
  const showLockUnlockButton = farm.id && canLock && !isDeleting;
  // a farm can be deleted if it is not a new farm and the user has permission to delete
  // and the farm is already archived
  const showDeleteFarmButton = farm.id && canDelete && farm.isArchived;
  // the farm settings can be cancelled if passed an on cancel
  // and if the deletion confirmation flow is not in progress
  // and if the settings are not in a modal (the modal uses an x in the top right to close)
  const showCancelButton = onCancel && !isDeleting && !inModal;
  // a farm can be duplicated if it is not a new farm and the farm is not archived
  const { isSuperadmin } = useUserRole();
  const showDuplicateFarmButton =
    farm.id && farmCanBeDuplicated(farm, isSuperadmin);

  // ---- Row 3 ----
  // a farm can be shared if it is not a new farm and the farm is not archived
  // and the user is able to share and the user is on mobile
  const showShareFarmButton =
    farm.id && userCanEditFarm && !farm.isArchived && isMobile && canShare;

  return (
    <>
      <ButtonRow>
        {showViewButton && <ViewFarmButton farm={farm} loading={loading} />}
        {userCanEditFarm && (
          <FullWidthSubmitButton block>
            {submitButtonText}
          </FullWidthSubmitButton>
        )}
      </ButtonRow>
      <ButtonRow>
        {showDuplicateFarmButton && (
          <DuplicateFarmButton
            farm={farm}
            mutateAndSetState={mutateAndSetState}
            loading={loading}
          />
        )}
        {showLockUnlockButton && (
          <LockOrUnlockButton
            farm={farm}
            mutateAndSetState={mutateAndSetState}
          />
        )}
        {showArchiveUnarchiveButton && (
          <ArchiveOrUnarchiveButton
            farm={farm}
            mutateAndSetState={mutateAndSetState}
          />
        )}
        {showDeleteFarmButton && (
          <DeleteButtonWithConfirm
            farm={farm}
            isDeleting={isDeleting}
            setIsDeleting={setIsDeleting}
            mutateAndSetState={mutateAndSetState}
          />
        )}
        {showCancelButton && (
          <FullWidthButton
            onClick={onCancel}
            disabled={isSubmitting || loading}
          >
            Cancel
          </FullWidthButton>
        )}
      </ButtonRow>
      <ButtonRow>
        {showShareFarmButton && (
          <ShareFarmButton farmId={farm.id} className="w-100" />
        )}
      </ButtonRow>
      {loading && (
        <Delayed delay={500}>
          <>
            <p className="text-info">Please wait...</p>
            <img
              alt="Loading gif"
              src="https://media1.tenor.com/images/fbe689921ca7560bd6020fac9b4d0633/tenor.gif"
            />
          </>
        </Delayed>
      )}
    </>
  );
};

export default FarmFormButtonBlock;
